import React, { useState, useEffect } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { ReactComponent as Underline } from "../../../icons/underline design.svg";
import { useDebounce } from "../../../hooks/useDebounce";
import Select from "react-select";

const SectionHeader = ({
  title = "",
  placeholder = "Search…",
  onSearch = null,
  className = "",
  language = "",
  onLanguageChange = () => {},
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 500);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    handleResize(); // set initially
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedQuery);
    }
  }, [debouncedQuery, onSearch]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleClear = () => {
    setQuery("");
    if (onSearch) onSearch("");
  };

  const toggleSearch = () => {
    if (!isMobile) return; // Prevent toggle on desktop
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => {
        const input = document.getElementById("floating-search");
        if (input) input.focus();
      }, 100);
    }
  };

  const languageOptions = [
    { value: "", label: "All" },
    { value: "ENGLISH", label: "English" },
    { value: "MARATHI", label: "Marathi" },
    { value: "HINDI", label: "Hindi" },
    { value: "GUJARATI", label: "Gujarati" },
  ];

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: "0.25rem 0.5rem",
      borderRadius: "0.5rem",
      borderColor: state.isFocused ? "#FF864A" : "#E0E0E0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(255, 134, 74, 0.5)" : "none",
      "&:hover": {
        borderColor: "#FF864A",
      },
      backgroundColor: "#fff",
      minHeight: "2.5rem",
      fontSize: "0.875rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#FF864A"
        : state.isFocused
        ? "#FFE4D9"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      fontSize: "0.875rem",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999, // High enough to appear above all other elements
      position: "absolute",
    }),
  };

  return (
    <div
      className={`bg-primary-shade1 border-b border-[#FF7236] py-4 ${className} z-30 relative`}
    >
      <h2 className="text-3xl font-medium text-center text-[#171D1B]">
        {title}
      </h2>
      <div className="flex justify-center">
        <Underline className="h-4" />
      </div>

      {/* Mobile Language Selector */}
      <div className="sm:hidden flex items-center justify-center gap-2 mt-3 px-4">
        <label className="text-xs text-gray-700 font-medium">Language:</label>
        <div className="w-[140px] relative z-20">
          <Select
            options={languageOptions}
            styles={customSelectStyles}
            value={languageOptions.find((opt) => opt.value === language)}
            onChange={(selected) => onLanguageChange(selected.value)}
            isSearchable={false}
            menuPlacement="auto"
          />
        </div>
      </div>

      {onSearch && (
        <div
          className={`
            fixed bottom-4 right-4 z-50
            sm:static sm:right-0 sm:bottom-auto sm:mt-6
            flex flex-col sm:flex-row sm:justify-center sm:items-center gap-4 px-4
          `}
        >
          {/* Search Bar */}
          <div className="relative w-full sm:w-[600px] max-w-full">
            <AnimatePresence mode="wait">
              {!isMobile || isOpen ? (
                <motion.div
                  key="searchBar"
                  initial={{
                    opacity: isMobile ? 0 : 1,
                    scale: isMobile ? 0.95 : 1,
                  }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                >
                  <input
                    id="floating-search"
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    className="w-full rounded-full border border-gray-300 bg-white px-5 py-3 pr-20 shadow-md placeholder:text-[#FF7236] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF7236] focus:border-[#FF7236] transition-all duration-300"
                  />

                  {query && (
                    <button
                      onClick={handleClear}
                      className="absolute inset-y-0 right-14 flex items-center justify-center text-[#FF7236] hover:text-[#EB5E22]"
                      aria-label="Clear search"
                    >
                      <FiX className="h-5 w-5" />
                    </button>
                  )}

                  <button
                    onClick={toggleSearch}
                    className="absolute inset-y-0 right-0 flex items-center justify-center rounded-full bg-[#FF7236] p-3 hover:bg-[#EB5E22] text-white shadow-md"
                    aria-label="Search"
                  >
                    <FiSearch className="h-5 w-5" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="floatingIcon"
                  onClick={toggleSearch}
                  className="absolute right-0 bottom-0 bg-[#FF7236] p-4 rounded-full shadow-lg text-white hover:bg-[#EB5E22] focus:outline-none"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <FiSearch className="h-5 w-5" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <div className="hidden sm:flex w-full sm:w-auto flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <label className="text-sm text-gray-700 font-medium">
              Language
            </label>
            <div className="w-full sm:w-[150px] relative z-50">
              <Select
                options={languageOptions}
                styles={customSelectStyles}
                value={languageOptions.find((opt) => opt.value === language)}
                onChange={(selected) => onLanguageChange(selected.value)}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionHeader;
